/**
 * Vorlesen der Frage per Browser-eigener Web Speech API (Wunsch Niggo
 * 2026-09-13) - bewusst keine Bibliothek/kein Dienst, genau wie bei den
 * Sound-Effekten (sound.ts): keine neue Abhängigkeit, keine Kosten. Liest
 * NUR den Fragetext vor (Server schickt dafür extra `ui:question`, getrennt
 * vom normalen Terminal-Text) - nichts anderes wird automatisch vorgelesen.
 *
 * Stimme + Lautstärke sind eigene Einstellungen, unabhängig von den
 * Spielsounds (sound.ts) - eigenes Popup statt nur einem Ein/Aus-Knopf
 * (Wunsch Niggo 2026-09-13). Die Stimmliste kommt vollständig vom Browser/
 * Betriebssystem (`speechSynthesis.getVoices()`) - unter Edge/Windows sind
 * darunter i. d. R. auch Microsofts kostenlose "Online (Natural)"-Stimmen,
 * ohne dass wir dafür etwas Eigenes anbinden müssten.
 *
 * Standardmäßig AUS (nicht jeder will das), Zustand in localStorage gemerkt.
 * Nur im Web - der SSH-Client bekommt das bewusst nicht. Auf vielen
 * Mobilgeräten/Browsern ist die Web Speech API gar nicht verfügbar - dann
 * bleibt `supported` false und die Oberfläche blendet die Steuerung aus,
 * statt eine Funktion vorzutäuschen, die nicht funktioniert.
 */
const STORAGE_KEY = 'dq.tts.v2';

interface StoredSettings {
  enabled: boolean;
  voiceURI: string | null;
  volume: number;
}

function loadStored(): StoredSettings {
  const fallback: StoredSettings = { enabled: false, voiceURI: null, volume: 1 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<StoredSettings>;
    return {
      enabled: Boolean(parsed.enabled),
      voiceURI: typeof parsed.voiceURI === 'string' ? parsed.voiceURI : null,
      volume: typeof parsed.volume === 'number' ? Math.min(1, Math.max(0, parsed.volume)) : 1,
    };
  } catch {
    return fallback; // privater Modus o. ä.
  }
}

class TtsManager {
  private state: StoredSettings = loadStored();

  get enabled(): boolean {
    return this.state.enabled;
  }

  get voiceURI(): string | null {
    return this.state.voiceURI;
  }

  /** 0-100, für Slider/Anzeige - getrennt von der Spiellautstärke (sound.ts). */
  get volumePercent(): number {
    return Math.round(this.state.volume * 100);
  }

  get supported(): boolean {
    return 'speechSynthesis' in window;
  }

  /** Verfügbare Stimmen des Browsers/Betriebssystems - kann direkt nach dem Laden noch leer sein, siehe onVoicesChanged(). */
  getVoices(): SpeechSynthesisVoice[] {
    return this.supported ? window.speechSynthesis.getVoices() : [];
  }

  /** Manche Browser laden die Stimmliste asynchron nach - danach neu abfragen. */
  onVoicesChanged(cb: () => void): void {
    if (this.supported) window.speechSynthesis.addEventListener('voiceschanged', cb);
  }

  setEnabled(enabled: boolean): void {
    this.state.enabled = enabled;
    if (!enabled) window.speechSynthesis?.cancel();
    this.persist();
  }

  setVoice(voiceURI: string | null): void {
    this.state.voiceURI = voiceURI;
    this.persist();
  }

  setVolume(percent: number): void {
    this.state.volume = Math.min(1, Math.max(0, percent / 100));
    this.persist();
  }

  speak(text: string): void {
    if (!this.state.enabled || !this.supported) return;
    window.speechSynthesis.cancel(); // vorherige Frage abbrechen, falls noch am Vorlesen
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'de-DE';
    utterance.volume = this.state.volume;
    const voice = this.getVoices().find((v) => v.voiceURI === this.state.voiceURI);
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* ignorieren */
    }
  }
}

export const tts = new TtsManager();
